import { useState } from "react";
import { MOD_LABEL, useKeyboardShortcut } from "./useKeyboardShortcut";

/**
 * Global cheatsheet of keyboard shortcuts. Press {@code ?} (Shift+/) to
 * open; press Esc or click the backdrop to close.
 *
 * <p>This is intentionally a static list — the alternative (a runtime
 * registry that every shortcut registers into) gives no real value: the
 * list of shortcuts is small enough to maintain by hand, and a static
 * source-of-truth is easier to scan than a generated one.
 */
export function ShortcutsCheatsheet() {
  const [open, setOpen] = useState(false);

  useKeyboardShortcut(
    { key: "?", shift: true, allowInModal: true },
    () => setOpen((o) => !o),
    []
  );
  useKeyboardShortcut(
    { key: "escape", allowInInput: true, allowInModal: true },
    () => setOpen(false),
    []
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="mt-[6vh] w-full max-w-[540px] rounded-lg border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Phím tắt</h2>
          <button
            type="button"
            className="rounded-md px-2 text-xl leading-none text-muted-foreground hover:bg-muted"
            onClick={() => setOpen(false)}
            aria-label="Đóng"
          >
            ×
          </button>
        </div>

        <Section title="Khắp ứng dụng">
          <Row keys={["?"]} label="Mở / đóng bảng phím tắt" />
          <Row keys={["Esc"]} label="Đóng cửa sổ đang mở" />
        </Section>

        <Section title="Trên lịch hẹn">
          <Row keys={["←"]} label="Tuần / ngày trước" />
          <Row keys={["→"]} label="Tuần / ngày sau" />
          <Row keys={["T"]} label="Về hôm nay" />
          <Row keys={["N"]} label="Tạo lịch hẹn mới" />
          <Row keys={["D"]} label="Chế độ xem theo ngày" />
          <Row keys={["W"]} label="Chế độ xem theo tuần" />
        </Section>

        <Section title="Trong lần khám (Encounter)">
          <Row keys={["1"]} label="Tab dấu hiệu sinh tồn" />
          <Row keys={["2"]} label="Tab phiếu khám (SOAP)" />
          <Row keys={["3"]} label="Tab chẩn đoán" />
          <Row keys={["4"]} label="Tab đơn thuốc" />
          <Row keys={["5"]} label="Tab tệp đính kèm" />
          <Row keys={[MOD_LABEL, "Shift", "S"]} label="Ký + kết thúc lần khám" />
          <Row keys={[MOD_LABEL, "P"]} label="In phiếu tóm tắt" />
        </Section>

        <p className="mt-3 text-xs text-muted-foreground">
          Lưu ý: các phím một-ký-tự (n / t / 1-5 / ←) không kích hoạt khi đang
          gõ trong ô nhập văn bản. Các tổ hợp với <kbd>{MOD_LABEL}</kbd> vẫn hoạt
          động bình thường.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <h3 className="mb-1.5 text-[13px] text-muted-foreground">{title}</h3>
      <table className="w-full border-collapse">
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Row({ keys, label }: { keys: string[]; label: string }) {
  return (
    <tr>
      <td className="w-[180px] py-1">
        {keys.map((k, i) => (
          <span key={k + i}>
            <kbd className="inline-block min-w-[22px] rounded border bg-muted px-1.5 py-0.5 text-center font-mono text-xs">
              {k}
            </kbd>
            {i < keys.length - 1 && <span className="mx-1 text-muted-foreground">+</span>}
          </span>
        ))}
      </td>
      <td className="py-1 text-sm">{label}</td>
    </tr>
  );
}
